import {
  FloatingArrow,
  FloatingArrowProps,
  FloatingPortal,
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Transition } from "@headlessui/react";
import React, { ReactElement, cloneElement, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const ARROW_HEIGHT = 7;

export type OverlayTriggerType = "hover" | "click" | "focus";
export type OverlayRole =
  | "tooltip"
  | "dialog"
  | "alertdialog"
  | "menu"
  | "listbox"
  | "grid"
  | "tree";

export interface ArrowProps extends Omit<FloatingArrowProps, "context"> {}

export interface OverlayTriggerProps {
  children: ReactElement | (({ opened }: { opened: boolean }) => ReactElement);
  overlay:
    | ReactElement
    | (({ dismiss }: { dismiss: () => void }) => ReactElement);
  trigger?: OverlayTriggerType | OverlayTriggerType[];
  placement?: Placement;
  overlayRole?: OverlayRole;
  showArrow?: boolean;
  arrowProps?: ArrowProps;
  className?: string;
}

const OverlayTrigger = (props: Readonly<OverlayTriggerProps>) => {
  const {
    children,
    overlay,
    placement = "bottom",
    trigger = ["hover", "focus"],
    overlayRole = "dialog",
    showArrow = true,
    arrowProps = { height: ARROW_HEIGHT },
    className,
  } = props;

  const [show, setShow] = useState(false);

  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: show,
    onOpenChange: setShow,
    middleware: [
      offset(ARROW_HEIGHT),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
    placement: placement,
  });

  const triggers: OverlayTriggerType[] = [].concat(trigger as any);

  const click = useClick(context, {
    enabled: triggers.indexOf("click") !== -1,
  });
  const focus = useFocus(context, {
    enabled: triggers.indexOf("focus") !== -1,
  });
  const hover = useHover(context, {
    enabled: triggers.indexOf("hover") !== -1,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: overlayRole });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    focus,
    hover,
    dismiss,
    role,
  ]);

  const triggerProps: any = {
    ref: refs.setReference,
    ...getReferenceProps(),
  };

  const overlayProps: any = {
    ref: refs.setFloating,
    style: floatingStyles,
    ...getFloatingProps(),
  };

  const renderChildren =
    typeof children === "function" ? children({ opened: show }) : children;

  const renderOverlay =
    typeof overlay === "function"
      ? overlay({ dismiss: () => setShow(false) })
      : overlay;

  return (
    <>
      {cloneElement(renderChildren, triggerProps)}
      {
        <FloatingPortal>
          <Transition
            show={show}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            className={twMerge("absolute z-[1046] w-full", className)}
          >
            {showArrow
              ? cloneElement(
                  renderOverlay,
                  overlayProps,
                  (
                    React.Children.map(
                      renderOverlay.props.children,
                      (child) => child,
                    ) ?? []
                  ).concat(
                    <FloatingArrow
                      key="arrow"
                      ref={arrowRef}
                      context={context}
                      {...arrowProps}
                    />,
                  ),
                )
              : cloneElement(renderOverlay, overlayProps)}
          </Transition>
        </FloatingPortal>
      }
    </>
  );
};

export default OverlayTrigger;
